/**
 * Atlas PM · 任务设计器（P3.7b）· 本地草稿类型
 *
 * ⚠️ 本文件的字段是**前端本地状态**，提交时由 `api.ts` 映射为后端 DTO。
 *    后端 DTO 见 apps/api/src/modules/{task,metric,deliverable}/dto/*.ts，
 *    全局 ValidationPipe 开启 whitelist + forbidNonWhitelisted，
 *    故本地草稿字段名不要直接透传。
 */

import { ELEMENT_TYPE, type ElementType } from '@atlaspm/shared';

/** 指标类型（对应设计稿左侧模板徽标；后端 metricType 为自由字符串） */
export const METRIC_TYPE_OPTIONS = [
  { value: 'PERCENT', label: '百分比' },
  { value: 'BOOL', label: '布尔' },
  { value: 'NUMBER', label: '数值' },
  { value: 'DATE', label: '日期' },
] as const;

export type MetricTypeCode = (typeof METRIC_TYPE_OPTIONS)[number]['value'];

export function metricTypeLabel(code: string): string {
  return METRIC_TYPE_OPTIONS.find((o) => o.value === code)?.label ?? code;
}

/** 要素草稿 */
export interface ElementDraft {
  key: string;
  /**
   * ⭐ P7.5：后端要素 id。
   *   - 新建模式下为 undefined（提交走 POST /metrics/:id/elements）
   *   - 编辑模式下从 GET /tasks/:taskId/metrics 回填（提交走 PATCH /elements/:id）
   *   用于差异比对（diff）：带 id = 已存在，无 id = 本次新增。
   */
  id?: string;
  name: string;
  /** 描述 ≤30 字（按字符数计） */
  description: string;
  elementType: ElementType;
  /** 设计稿上的动作徽标：填数值 / 传附件 / 勾选项 / 选日期 / 填文本 / 选枚举 */
  uiLabel: string;
  isRequired: boolean;
  /** 占指标权重 1-100（单要素时可独占 100；同一指标下之和须 = 100） */
  weight: number;
  /** 完成度 0-100（用户填） */
  completionPct: number;
  /** 完成质量 0-100（用户填） */
  qualityPct: number;
  /** 是否需要附件 */
  requireAttachment: boolean;
  constraints?: Record<string, unknown>;
  /** 约束的自然语言描述，如「约束：0–999 · 单位「个」」 */
  constraintText?: string;
  helpText?: string;
}

/** 指标草稿（数字类字段用 string 存，便于受控输入编辑） */
export interface MetricDraft {
  key: string;
  /**
   * ⭐ P7.5：后端指标 id。
   *   - 新建模式下为 undefined（提交走 POST /tasks/:taskId/metrics）
   *   - 编辑模式下回填（提交走 PATCH /metrics/:id）
   */
  id?: string;
  name: string;
  metricType: string;
  unit: string;
  /** 占任务权重 0-100（所有指标之和须 = 100） */
  weight: string;
  /**
   * ⭐ P7.11：用户是否手动改过本指标权重。
   *   - false / undefined（默认）：该权重由系统自动分配（「权重自动填满」），
   *     增删指标时会重新平摊，保证合计尽快趋近 100；
   *   - true：用户显式输入过，**任何自动分配都不得覆盖**。
   *   编辑任务回填的存量指标一律标记为 true（不得改写已有数据）。
   */
  weightTouched?: boolean;
  elements: ElementDraft[];
}

/** 任务主体草稿 */
export interface TaskMetaDraft {
  projectId: string;
  name: string;
  ownerId: string;
  categoryId: string;
  /**
   * ⭐ P8.6：当前任务形态名称，仅用于编辑存量任务时回显「（已停用）」，
   *   不参与提交（提交按字段白名单构造 payload）。
   */
  categoryName?: string | null;
  description: string;
  priority: string;
  /**
   * ⭐ P7.7：计划起止时间（`YYYY-MM-DD`，对应后端 task.plan_start / plan_end，DATE 型）。
   *   空串 = 未设置。逾期判定完全依赖 planEnd（plan_end < 今天 即逾期），
   *   此前表单无入口导致字段恒为 null，逾期功能形同虚设。
   */
  planStart: string;
  planEnd: string;
}

let seq = 0;
/** 本地稳定 key（仅用于 React list key 与增删定位，不入库） */
export function draftKey(prefix = 'k'): string {
  seq += 1;
  return `${prefix}_${Date.now().toString(36)}_${seq}`;
}

/**
 * 空白指标（「+ 添加指标」用）。
 *
 * ⭐ P7.11「权重自动填满」：默认权重 **100** 且标记为「未手动设置」。
 *   - 只有一个指标时，直接独占 100，无需手填即可提交；
 *   - 新增更多指标时，由 TaskDesigner 对「未手动设置」者自动平摊（见 rebalanceUntouchedWeights）。
 */
export function emptyMetricDraft(): MetricDraft {
  return {
    key: draftKey('m'),
    name: '新指标',
    metricType: 'PERCENT',
    unit: '%',
    weight: '100',
    weightTouched: false,
    elements: [emptyElementDraft()],
  };
}

/** 空白要素（新指标默认带 1 个；权重默认 100，便于单要素指标直接成立） */
export function emptyElementDraft(): ElementDraft {
  return {
    key: draftKey('e'),
    name: '新要素',
    description: '',
    elementType: ELEMENT_TYPE.TEXT,
    uiLabel: '填文本',
    isRequired: true,
    weight: 100,
    completionPct: 0,
    qualityPct: 0,
    requireAttachment: false,
    constraintText: '自由填写',
  };
}
